import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db4nc8mk6jkc2oct498g";

interface FormApiResult {
  code?: string;
  meta?: { message?: string; detail?: string };
  message?: string;
}

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "contact_alt") return;
      payload.append(key, String(value));
    });

    try {
      setStatus("submitting");
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormApiResult | null = null;
      try {
        parsed = JSON.parse(responseText) as FormApiResult;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const okCode = parsed?.code === "OK";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!response.ok || !okCode || isSpam) {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      id="about-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6"
    >
      <h4 className="font-heading font-bold text-lg text-foreground-950 mb-1">
        <a href="#about-contact-form" className="hover:text-primary-500 transition-colors">
          给我们留言
        </a>
      </h4>
      <p className="text-sm text-foreground-500 mb-5">内容建议、合作咨询或使用反馈，欢迎告诉我们。</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
            您的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入称呼"
            className="w-full h-11 px-3.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
            联系邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full h-11 px-3.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
          />
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言类型
        </label>
        <select
          id="contact-topic"
          name="topic"
          className="w-full h-11 px-3.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
        >
          <option value="内容建议">内容建议</option>
          <option value="合作咨询">合作咨询</option>
          <option value="使用反馈">使用反馈</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mb-5">
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请输入您的留言内容（不超过 500 字）"
          className="w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors resize-none"
        ></textarea>
      </div>

      <input
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="contact-form-note"
      />

      {status === "success" && (
        <div className="mb-4 flex items-center gap-2 rounded-md bg-accent-100 text-accent-700 text-sm px-3.5 py-2.5">
          <i className="ri-checkbox-circle-line text-base"></i>
          留言已提交成功，感谢你的反馈！
        </div>
      )}
      {status === "error" && (
        <div className="mb-4 flex items-center gap-2 rounded-md bg-primary-100 text-primary-700 text-sm px-3.5 py-2.5">
          <i className="ri-error-warning-line text-base"></i>
          {formError}
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full sm:w-auto h-11 px-8 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors"
      >
        {status === "submitting" ? "提交中…" : "提交留言"}
      </button>
    </form>
  );
}